import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { groupBy, isNil, minBy, maxBy, range } from 'lodash'
import MessageCard from '../../../../../components/card/MessageCard'
import i18n from 'simple-react-i18n'
import React from 'react'
import QualityOverviewTableDto from './QualityOverviewTableDto'
import { getLabel, getLabelTruncate, getTruncate } from '../../../../../utils/StoreUtils'
import QualityOverviewTableStatsDto from './QualityOverviewTableStatsDto'
import { getBeginingOfTheYear, getDate, getEndOfTheYear } from '../../../../../utils/DateUtil'
import { getStatuses } from '../../../../../utils/QualityUtils'
import { nbPerPageLabel30 } from '../../../../../referencial/constants/ReferencialConstants'
import useActions from '../../../../../utils/customHook/useActions'
import PropTypes from 'prop-types'
import useListIndexed from 'utils/customHook/useListIndexed'
import { ALIGN, CardTable } from 'components/datatable/NewTable'
import { push } from '@lagunovsky/redux-react-router'
import { getStationDesignation } from 'utils/StationUtils'
import { SHOW_INSEE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { substringText } from 'utils/StringUtil'
import { darkBlue } from 'utils/constants/ColorTheme'
import { RESEARCH_TYPE } from '../../../../constants/QualityConstants'

const getStatusColor = status => {
    switch (status) {
        case 3:
            return 'green'
        case 2:
            return 'lightgreen'
        case 4:
            return 'blue'
        default:
            return 'grey'
    }
}

// par mode de recherche : libellés des 2 colonnes de comptage, et onglet station visé au clic
const RESEARCH_TYPE_DISPLAY = {
    [RESEARCH_TYPE.WATER]: { countLabel: () => i18n.nbSample, detailLabel: () => i18n.analysis, tab: 'suiviPC' },
    [RESEARCH_TYPE.TAXON]: { countLabel: () => i18n.operations, detailLabel: () => i18n.taxons, tab: 'hydrobioMonitoring' },
    [RESEARCH_TYPE.INDICES]: { countLabel: () => i18n.operations, detailLabel: () => i18n.indices, tab: 'hydrobioMonitoring' },
    [RESEARCH_TYPE.STATES]: { countLabel: () => i18n.states, tab: 'qualityStates' },
}

const QualityIntegrationOverviewTable = ({
    operationResults = [],
    operationStats = [],
    stations = [],
    researchType = RESEARCH_TYPE.WATER,
}) => {
    const display = RESEARCH_TYPE_DISPLAY[researchType] || RESEARCH_TYPE_DISPLAY[RESEARCH_TYPE.WATER]
    const {
        citiesIndex,
    } = useSelector(store => ({
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const minYear = minBy(operationResults, 'year')?.year
    const maxYear = maxBy(operationResults, 'year')?.year
    const listOfYear = range(minYear, maxYear + 1, 1).reverse()

    const groups = groupBy(operationResults, 'id')
    const groupsStats = useListIndexed(operationStats)

    const dispatch = useDispatch()

    const data = stations.map(qualito => {
        const stats = groupsStats[qualito.id]

        const cityLabel = qualito?.townCode ? citiesIndex[qualito?.townCode]?.name : ''

        const baseObj = {
            code: { value: getStationDesignation(qualito), cellType: 'string' },
            exportCode: { value: getStationDesignation(qualito) },
            name: { value: getTruncate(qualito?.name, 25), tooltip: qualito?.name?.length > 25 ? qualito?.name : undefined },
            exportName: { value: qualito?.name },
            city: { value: showInseeCode ? getLabelTruncate(cityLabel, qualito?.townCode, 25) : substringText(cityLabel, 25), tooltip: cityLabel?.length > 25 ? cityLabel : undefined },
            exportCity: { value: getLabelTruncate(cityLabel, qualito?.townCode, Number.POSITIVE_INFINITY) },
            startDate: { value: getDate(stats?.start), sortValue: stats?.start, format: 'dd/MM/yyyy', cellType: 'date' },
            endDate: { value: getDate(stats?.end), sortValue: stats?.end, format: 'dd/MM/yyyy', cellType: 'date' },
            nbSample: { value: stats?.operations || 0 },
            analysis: { value: stats?.analysis || 0 },
            id: { value: qualito?.id },
        }

        const years = groups[qualito.id] || []

        const yearProps = years.reduce((acc, yearObj) => {
            acc[yearObj.year] = {
                value: yearObj.count,
                color: getStatusColor(yearObj.status),
                // les états n'ont pas de statut d'opération : pas de libellé à afficher
                tooltip: isNil(yearObj.status) ? undefined : getLabel(getStatuses(), yearObj.status, undefined, undefined, i18n.brute),
                onClick: () => {
                    dispatch(push(`/station/quality/${qualito.id}/${display.tab}?startDate=${getBeginingOfTheYear(yearObj.year)}&endDate=${getEndOfTheYear(yearObj.year)}`))
                },
            }
            return acc
        }, {})

        return { ...baseObj, ...yearProps }
    })

    const headers = [
        { key: 'code', sticky: ALIGN.LEFT, width: 150 },
        { key: 'city', sticky: ALIGN.LEFT, width: 300 },
        { key: 'name', sticky: ALIGN.LEFT, width: 250 },
        'startDate',
        'endDate',
        { key: 'nbSample', value: display.countLabel() },
        ...(display.detailLabel ? [{ key: 'analysis', value: display.detailLabel() }] : []),
        ...listOfYear.map(y => `${y}`),
    ]

    useActions(() => {
        const exportData = data.map((d, i) => {
            const returnData = { ...d, code: d.exportCode, name: d.exportName, city: d.exportCity }
            return i === 0 ? { ...returnData, headers: headers.map(h => h?.key || h) } : returnData
        })
        return {
            export: () => {
                return {
                    data: exportData,
                    exportType: 'xlsx',
                    titleFile: i18n.integrationOverview,
                }
            },
        }
    }, [data])

    if (!operationResults.length) {
        return <MessageCard>{i18n.noOperationForTheseCriteria}</MessageCard>
    }

    return (
        <div className='padding-top-1 padding-left-1 padding-right-1'>
            <CardTable
                title={i18n.watchpoints}
                rows={data}
                headers={headers}
                rowsPerPageOptions={nbPerPageLabel30}
                color={darkBlue}
                onClickRow={ (e) => {
                    dispatch(push(`/station/quality/${e.id.value}/${display.tab}`))
                }}
            />
        </div>
    )
}

QualityIntegrationOverviewTable.propTypes = {
    operationResults: PropTypes.arrayOf(PropTypes.instanceOf(QualityOverviewTableDto)),
    operationStats: PropTypes.arrayOf(PropTypes.instanceOf(QualityOverviewTableStatsDto)),
    stations: PropTypes.shape({/* qualitometer */}),
    researchType: PropTypes.number,
}

export default QualityIntegrationOverviewTable
